import React, { useEffect } from "react";
import Button from "../../../../common/ekedc-components/button/Button";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import styles from "./transfer-unit-complete-modal.module.scss";

const TransferDownloadIcon: React.FC = () => (
	<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
		<path
			d="M7 1.75v8.17M3.5 6.42 7 9.92l3.5-3.5M2.33 11.67h9.34"
			stroke="var(--app-primary-text)"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const TransferShareIcon: React.FC = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
		<circle cx="3.3" cy="8" r="1.7" stroke="var(--app-primary-text)" strokeWidth="1.3" fill="none" />
		<circle cx="12.3" cy="3.7" r="1.7" stroke="var(--app-primary-text)" strokeWidth="1.3" fill="none" />
		<circle cx="12.3" cy="12.3" r="1.7" stroke="var(--app-primary-text)" strokeWidth="1.3" fill="none" />
		<path d="M4.8 7.2 10.8 4.4M4.8 8.8l6 2.8" stroke="var(--app-primary-text)" strokeWidth="1.3" strokeLinecap="round" />
	</svg>
);

const TransferStepDoneIcon: React.FC = () => (
	<svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden>
		<circle cx="11" cy="11" r="11" fill="var(--app-status-success)" />
		<path
			d="M6.5 11.2 9.5 14l6-6.4"
			stroke="var(--app-base-white)"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

const TransferStepPendingIcon: React.FC = () => (
	<svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden>
		<circle cx="11" cy="11" r="10" stroke="var(--app-tooltip-bg)" strokeWidth="1.8" fill="none" />
		<path d="M11 6v5l3 2" stroke="var(--app-tooltip-bg)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const formatTimestamp = (d: Date): string => {
	const day = d.getDate();
	const month = d.toLocaleString("en-US", { month: "short" });
	const year = d.getFullYear();
	const hours24 = d.getHours();
	const minutes = d.getMinutes().toString().padStart(2, "0");
	const hours12 = ((hours24 + 11) % 12) + 1;
	const ampm = hours24 >= 12 ? "pm" : "am";
	return `${day} ${month} ${year}, ${hours12}:${minutes} ${ampm}`;
};

interface TransferTimelineStep {
	label: string;
	timestamp: string;
	status: "done" | "pending";
}

interface TransferUnitCompleteModalProps {
	completedAt?: number;
	onBackHome: () => void;
	onDownload?: () => void;
	onShare?: () => void;
}

export const TransferUnitCompleteModal: React.FC<TransferUnitCompleteModalProps> = ({
	completedAt,
	onBackHome,
	onDownload,
	onShare,
}) => {
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onBackHome();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onBackHome]);

	const baseTime = completedAt ? new Date(completedAt) : new Date();
	const validationTime = new Date(baseTime);
	const creditTime = new Date(baseTime.getTime() + 60_000);

	const steps: TransferTimelineStep[] = [
		{ label: "Payment", timestamp: formatTimestamp(baseTime), status: "done" },
		{ label: "Validation", timestamp: formatTimestamp(validationTime), status: "done" },
		{ label: "Unit Credited", timestamp: formatTimestamp(creditTime), status: "pending" },
	];

	return (
		<CommonDialog onCloseDialog={onBackHome} noPadding={true} borderRadius="21.67px">
			<div className={styles.modal}>
				<header className={styles.topRow}>
					<button type="button" className={styles.iconPill} onClick={onDownload} disabled={!onDownload}>
						<span className={styles.iconPillLabel}>Download</span>
						<TransferDownloadIcon />
					</button>
					<button type="button" className={styles.iconPill} onClick={onShare} disabled={!onShare}>
						<TransferShareIcon />
						<span className={styles.iconPillLabel}>Share</span>
					</button>
				</header>

				<div className={styles.body}>
					<div className={styles.hero}>
						<img
							src={`${process.env.PUBLIC_URL ?? ""}/gifs/checkmark.gif`}
							alt="Transfer request sent"
							width={150}
							height={150}
						/>
					</div>

					<div className={styles.intro}>
						<h1 className={styles.title}>Transfer Units Request Sent</h1>
						<p className={styles.subtitle}>
							We'll notify you once the transfer is complete or unsuccessful.
						</p>
					</div>

					<section className={styles.timelineCard}>
						{steps.map((step, idx) => (
							<div key={step.label} className={styles.timelineRow}>
								<div className={styles.timelineIconCol}>
									{step.status === "done" ? <TransferStepDoneIcon /> : <TransferStepPendingIcon />}
									{idx < steps.length - 1 && <span className={styles.timelineConnector} />}
								</div>
								<span className={styles.timelineLabel}>{step.label}</span>
								<span className={styles.timelineTimestamp}>{step.timestamp}</span>
							</div>
						))}
					</section>
				</div>

				<footer className={styles.footer}>
					<Button text="Back to Home" onClick={onBackHome} />
				</footer>
			</div>
		</CommonDialog>
	);
};

export default TransferUnitCompleteModal;
